import activationApi from '@api/activationApi';
import deviceApi from '@api/deviceApi';
import locationApi from '@api/locationApi';
import { LoadingIndicator } from '@components/loadingIndicator';
import Tabs from '@components/tabs';
import Title from '@components/title';
import { css } from '@emotion/css';
import useIsMobile from '@hooks/useIsMobile';
import { useTranslator } from '@i18n';
import { ActionIcon, Checkbox, Group, Menu, Stack } from '@mantine/core';
import { setSerializer, type TableItem } from '@routes/inventory/-tableColumns/-helpers';
import batteryColumn from '@routes/inventory/-tableColumns/batteryColumn';
import descriptionColumn from '@routes/inventory/-tableColumns/descriptionColumn';
import idColumn from '@routes/inventory/-tableColumns/idColumn';
import locationColumn from '@routes/inventory/-tableColumns/locationColumn';
import mobileMainColumn from '@routes/inventory/-tableColumns/mobileMainColumn';
import registeredOnColumn from '@routes/inventory/-tableColumns/registeredOnColumn';
import sequenceNumberColumn from '@routes/inventory/-tableColumns/sequenceNumberColumn';
import stateColumn from '@routes/inventory/-tableColumns/stateColumn';
import typeColumn from '@routes/inventory/-tableColumns/typeColumn';
import { IconSettings } from '@tabler/icons-react';
import { Link, createFileRoute } from '@tanstack/react-router';
import chain from '@util/chain';
import { themeColor } from '@util/color';
import { useHandleError } from '@util/errors';
import { createUrlParam } from 'cross-state/react';
import { useMemo } from 'react';
import { Table, type Id, type Sort } from 'schummar-table';

export const Route = createFileRoute('/inventory/')({
  component: InventoryPage,
});

const defaultHiddenColumns = new Set<Id>(['id']);
const defaultSort = {
  mobile: [{ columnId: 'mobileMain', direction: 'asc' }],
  desktop: [
    { columnId: 'location', direction: 'asc' },
    { columnId: 'type', direction: 'desc' },
    { columnId: 'sequenceNumber', direction: 'asc' },
  ],
} satisfies Record<string, Sort[]>;

const url = {
  type: createUrlParam<string>({ type: 'hash', key: 'type', defaultValue: '' }),
  includeLocationless: createUrlParam<boolean>({ type: 'hash', key: 'includeLocationless', defaultValue: false }),
  hiddenColumns: createUrlParam<Set<Id>>({
    type: 'hash',
    key: 'hiddenColumns',
    defaultValue: defaultHiddenColumns,
    ...setSerializer<Id>(),
  }),
  sort: createUrlParam<Sort[]>({
    type: 'hash',
    key: 'sort',
  }),
};

const popoverClass = css({
  background: themeColor('paper', 6),
});

function InventoryPage() {
  const t = useTranslator();
  const { locationId } = Route.useSearch();
  const isMobile = useIsMobile();

  const [filterType = '', setFilterType] = url.type.useProp();
  const [includeLocationless, setIncludeLocationless] = url.includeLocationless.useProp();
  const [hiddenColumns, setHiddenColumns] = url.hiddenColumns.useProp();
  const [sort = isMobile ? defaultSort.mobile : defaultSort.desktop, setSort] = url.sort.useProp();

  const [locations, locationsError] = locationApi.lookup.useCache();
  const [devices, devicesError] = deviceApi.list({ locationId, includeLocationless }).useCache();
  const [activations, activationsError] = activationApi.allOpen(locationId ?? '').useCache({ disabled: !locationId });
  useHandleError([locationsError, devicesError, activationsError]);

  const items = useMemo(
    () =>
      chain(devices ?? [])
        .chain((_) => _?.filter((device) => !filterType || device.type === filterType))
        .chain((_) =>
          _?.map(
            (device) =>
              ({
                ...device,
                _location: device.location && locations?.get(device.location?.locationId),
                openActivations: activations?.filter((x) => x.deviceId === device.id),
              }) as TableItem,
          ),
        )
        .value(),
    [locations, devices, activations, filterType],
  );

  const counts: Record<string, number> = {
    '': devices?.length ?? 0,
    trap: devices?.filter((x) => x.type === 'trap').length ?? 0,
    gateway: devices?.filter((x) => x.type === 'gateway').length ?? 0,
  };

  return (
    <Title title={t('inventory.link')}>
      <Stack gap="xs">
        <Group
          gap="xs"
          align="center"
          css={{
            margin: 'var(--mantine-spacing-xs) var(--mantine-spacing-xs) 0',
          }}
        >
          <Tabs
            options={Object.keys(counts)}
            value={filterType}
            onChange={(type) => setFilterType(type)}
            renderLabel={(type) => (
              <Group gap="xs" wrap="nowrap">
                <span>{counts[type]}</span>
                <span>{t.dynamic(`inventory.filters.${type || 'all'}`)}</span>
              </Group>
            )}
            css={{
              flex: 1,
              maxWidth: 500,
            }}
          />

          {!locationId && (
            <Menu withArrow>
              <Menu.Target>
                <ActionIcon>
                  <IconSettings />
                </ActionIcon>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label>{t('inventory.settings.title')}</Menu.Label>
                <Menu.Item
                  leftSection={<Checkbox readOnly checked={includeLocationless} />}
                  onClick={() => setIncludeLocationless((value) => !value)}
                >
                  {t('inventory.settings.includeLocationless')}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>

        <Table
          items={items}
          id="id"
          fullWidth
          enableSelection={false}
          enableColumnSelection={!isMobile}
          enableExport={!isMobile}
          hiddenColumns={hiddenColumns}
          onHiddenColumnsChange={setHiddenColumns}
          sort={sort}
          onSortChange={setSort}
          onReset={() => {
            setHiddenColumns(defaultHiddenColumns);
            setSort(undefined);
          }}
          classes={{
            table: 'inventory-table',
            popover: popoverClass,
          }}
          colors={{
            background: 'var(--mantine-color-paper-7)',
            text: 'inherit',
            border: 'var(--mantine-color-paper-9)',
            borderLight: 'var(--mantine-color-paper-8)',
          }}
          styles={{
            headerCell: {
              top: 'var(--app-shell-header-height)',
              transform: 'var(--app-shell-header-transform)',
              transitionDuration: 'var(--app-shell-transition-duration)',
              transitionProperty: 'transform',
            },
            cell: {
              '&:not(:empty)': {
                paddingTop: '0.5em',
                paddingBottom: '0.5em',
              },
            },
          }}
          wrapRow={(props) => <div {...props} data-testid="inventory-item" />}
          wrapCell={(content, _value, item) => (
            <Link
              aria-label={`${t.dynamic(`entities.${item.type}`, {}, { fallback: item.type })} ${item.location?.sequenceNumber} ${item.location?.description}`}
              to="/inventory/$deviceType/$deviceId"
              params={{ deviceType: item.type, deviceId: item.id }}
              search={{ locationId: item.location?.locationId }}
              css={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {content}
            </Link>
          )}
          virtual
          enableColumnReorder={false}
          displaySize={{ mobile: 800, desktop: Infinity }}
          columns={[
            locationColumn,
            idColumn,
            stateColumn,
            typeColumn,
            sequenceNumberColumn,
            descriptionColumn,
            registeredOnColumn,
            batteryColumn,
            mobileMainColumn,
          ]}
        />

        {!devices && <LoadingIndicator />}
      </Stack>
    </Title>
  );
}
